import {ValidationError, NonJSONResponseError} from '../errors';

import type {Endpoints} from './types';
import {
    validateRegistrationRequestTokenBody,
    validateRegistrationRequestToken200,
    validateRegistrationRequestToken401,
    validateRegistrationRequestToken403,
    validateRegistrationRequestToken404,
    validateRegistrationRequestToken415,
    validateRegistrationRequestToken502,
    validateRegistrationRequestToken503,
    validateRegistrationRequestToken504,
    validateRegistrationRequestToken529,
    validateRegistrationExchangeToken200,
    validateRegistrationExchangeToken403,
    validateRegistrationExchangeToken404,
    validateRegistrationExchangeToken415,
    validateRegistrationExchangeToken502,
    validateRegistrationExchangeToken503,
    validateRegistrationExchangeToken504,
    validateRegistrationExchangeToken529,
    validateRegistrationRefreshToken200,
    validateRegistrationRefreshToken403,
    validateRegistrationReleaseTokenBody,
    validateRegistrationReleaseToken200,
    validateRegistrationReleaseToken403,
    validateRegistrations200,
} from './validation';

type RegistrationRequestTokenResponse =
    | {status: 200; data: Endpoints['registration_request_token']['200']}
    | {status: 401; data: Endpoints['registration_request_token']['401']}
    | {status: 403; data: Endpoints['registration_request_token']['403']}
    | {status: 404; data: Endpoints['registration_request_token']['404']}
    | {status: 415; data: Endpoints['registration_request_token']['415']}
    | {status: 502; data: Endpoints['registration_request_token']['502']}
    | {status: 503; data: Endpoints['registration_request_token']['503']}
    | {status: 504; data: Endpoints['registration_request_token']['504']}
    | {status: 529; data: Endpoints['registration_request_token']['529']};
export async function registrationRequestToken({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['registration_request_token']['Params'];
    body: Endpoints['registration_request_token']['Body'];
    host: string;
}): Promise<RegistrationRequestTokenResponse> {
    if (!validateRegistrationRequestTokenBody(body)) {
        throw new ValidationError(validateRegistrationRequestTokenBody.errors);
    }
    const res = await fetcher(
        `${host}/api/client/v2/registrations/${encodeURIComponent(params.alias)}/request_token?node=${encodeURIComponent(params.node)}&sso_token=${encodeURIComponent(params.ssoToken)}&chosen_idp=${encodeURIComponent(params.chosenIdp)}`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken200(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 401) {
        const data = await res.text();
        if (!validateRegistrationRequestToken401(data)) {
            throw new ValidationError(
                validateRegistrationRequestToken401.errors,
            );
        }
        return {status: res.status, data};
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken403(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 404) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken404(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken404.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 415) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken415(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken415.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 502) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken502(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken502.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 503) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken503(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken503.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 504) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken504(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken504.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 529) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRequestToken529(data)) {
                throw new ValidationError(
                    validateRegistrationRequestToken529.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type RegistrationRequestTokenMap =
    Endpoints['registration_request_token'];

type RegistrationExchangeTokenResponse =
    | {status: 200; data: Endpoints['registration_exchange_token']['200']}
    | {status: 403; data: Endpoints['registration_exchange_token']['403']}
    | {status: 404; data: Endpoints['registration_exchange_token']['404']}
    | {status: 415; data: Endpoints['registration_exchange_token']['415']}
    | {status: 502; data: Endpoints['registration_exchange_token']['502']}
    | {status: 503; data: Endpoints['registration_exchange_token']['503']}
    | {status: 504; data: Endpoints['registration_exchange_token']['504']}
    | {status: 529; data: Endpoints['registration_exchange_token']['529']};
export async function registrationExchangeToken({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['registration_exchange_token']['Params'];
    host: string;
}): Promise<RegistrationExchangeTokenResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/registrations/${encodeURIComponent(params.alias)}/exchange_token`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken200(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken403(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 404) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken404(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken404.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 415) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken415(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken415.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 502) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken502(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken502.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 503) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken503(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken503.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 504) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken504(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken504.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 529) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationExchangeToken529(data)) {
                throw new ValidationError(
                    validateRegistrationExchangeToken529.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type RegistrationExchangeTokenMap =
    Endpoints['registration_exchange_token'];

type RegistrationRefreshTokenResponse =
    | {status: 200; data: Endpoints['registration_refresh_token']['200']}
    | {status: 403; data: Endpoints['registration_refresh_token']['403']};
export async function registrationRefreshToken({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['registration_refresh_token']['Params'];
    host: string;
}): Promise<RegistrationRefreshTokenResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/registrations/${encodeURIComponent(params.alias)}/refresh_token`,
        {
            ...init,
            method: 'post',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRefreshToken200(data)) {
                throw new ValidationError(
                    validateRegistrationRefreshToken200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationRefreshToken403(data)) {
                throw new ValidationError(
                    validateRegistrationRefreshToken403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type RegistrationRefreshTokenMap =
    Endpoints['registration_refresh_token'];

type RegistrationReleaseTokenResponse =
    | {status: 200; data: Endpoints['registration_release_token']['200']}
    | {status: 403; data: Endpoints['registration_release_token']['403']};
export async function registrationReleaseToken({
    fetcher,
    init,
    params,
    body,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['registration_release_token']['Params'];
    body: Endpoints['registration_release_token']['Body'];
    host: string;
}): Promise<RegistrationReleaseTokenResponse> {
    if (!validateRegistrationReleaseTokenBody(body)) {
        throw new ValidationError(validateRegistrationReleaseTokenBody.errors);
    }
    const res = await fetcher(
        `${host}/api/client/v2/registrations/${encodeURIComponent(params.alias)}/release_token`,
        {
            ...init,
            method: 'post',

            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationReleaseToken200(data)) {
                throw new ValidationError(
                    validateRegistrationReleaseToken200.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }

    if (res.status === 403) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrationReleaseToken403(data)) {
                throw new ValidationError(
                    validateRegistrationReleaseToken403.errors,
                );
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type RegistrationReleaseTokenMap =
    Endpoints['registration_release_token'];

type RegistrationsResponse = {
    status: 200;
    data: Endpoints['registrations']['200'];
};
export async function registrations({
    fetcher,
    init,
    params,
    host,
}: {
    fetcher: typeof fetch;
    init?: RequestInit;
    params: Endpoints['registrations']['Params'];
    host: string;
}): Promise<RegistrationsResponse> {
    const res = await fetcher(
        `${host}/api/client/v2/registrations?q=${encodeURIComponent(params.query)}`,
        {
            ...init,
            method: 'get',
        },
    );

    if (res.status === 200) {
        if (res.headers.get('content-type')?.startsWith('application/json')) {
            const data: unknown = await res.json();
            if (!validateRegistrations200(data)) {
                throw new ValidationError(validateRegistrations200.errors);
            }
            return {status: res.status, data};
        } else {
            throw new NonJSONResponseError(
                res.headers.get('content-type'),
                await res.text(),
            );
        }
    }
    throw new Error(`Unexpected status ${res.status}`);
}
export type RegistrationsMap = Endpoints['registrations'];
